import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPrivate } from "@/lib/api";
import { Bell, CheckCheck, X } from "lucide-react";

const fmtRelative = (iso) => {
    if (!iso) return "";
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "ora";
        if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}g fa`;
        return new Date(iso).toLocaleDateString("it-IT");
    } catch { return iso; }
};

const TYPE_ICON = {
    assembly_convocation: "🗳️",
    new_document: "📄",
    reminder: "⏰",
    communication: "📢",
    default: "🔔",
};

export default function NotificationBell() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dropdownRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await apiPrivate.get("/notifications");
            setItems(r.data || []);
        } catch {
            /* ignore */
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60000); // poll ogni 60s
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const unread = items.filter(i => !i.read).length;

    const openNotif = async (n) => {
        try { await apiPrivate.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
        setOpen(false);
        if (n.link) navigate(n.link);
        load();
    };

    const markAllRead = async () => {
        try { await apiPrivate.post("/notifications/mark-all-read"); load(); } catch { /* ignore */ }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setOpen(!open)}
                className="relative p-2 rounded-full hover:bg-white/5 transition"
                aria-label="Notifiche"
                data-testid="notification-bell"
            >
                <Bell className="h-5 w-5 text-zinc-300" />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-vermillion text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-[#0a0c1c] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between p-4 border-b border-white/10">
                        <h3 className="font-display text-lg tracking-tighter">Notifiche</h3>
                        <div className="flex items-center gap-2">
                            {unread > 0 && (
                                <button onClick={markAllRead} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid="mark-all-read">
                                    <CheckCheck className="h-3 w-3" /> Segna tutte
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
                        </div>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {loading && items.length === 0 ? (
                            <div className="p-8 text-center text-zinc-500 text-sm">Caricamento…</div>
                        ) : items.length === 0 ? (
                            <div className="p-8 text-center text-zinc-500 text-sm">Nessuna notifica</div>
                        ) : (
                            items.map(n => (
                                <button
                                    key={n.id}
                                    onClick={() => openNotif(n)}
                                    className={`w-full text-left p-3 border-b border-white/5 flex items-start gap-3 hover:bg-white/5 transition ${!n.read ? "bg-magenta/5" : ""}`}
                                    data-testid={`notif-${n.id}`}
                                >
                                    <span className="text-xl leading-none">{TYPE_ICON[n.type] || TYPE_ICON.default}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm truncate ${!n.read ? "font-semibold text-white" : "text-zinc-400"}`}>{n.title}</span>
                                            {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-magenta shrink-0" />}
                                        </div>
                                        <div className="text-xs text-zinc-500 mt-0.5 truncate">{n.body}</div>
                                        <div className="text-[10px] text-zinc-600 mt-1 uppercase tracking-widest">{fmtRelative(n.created_at)}</div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
